import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { DEFAULT_LETTER_SPACING, DEFAULT_LINE_HEIGHT, MINIMUM_FONT_WEIGHT, MAXIMUM_FONT_WEIGHT, SUGGESTIONS_FONT_WEIGHT, DEFAULT_COMMANDS_TO_SKIP_SHELL, DEFAULT_LOCAL_ECHO_EXCLUDE } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminal';
import { isMacintosh, isWindows } from 'vscode/vscode/vs/base/common/platform';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { terminalColorSchema, terminalIconSchema } from '../../../../platform/terminal/common/terminalPlatformConfiguration.js';
import product$1 from 'vscode/vscode/vs/platform/product/common/product';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/workbench/common/configuration';

const _moduleId = "vs/workbench/contrib/terminal/common/terminalConfiguration";
const terminalDescriptors = '\n- ' + [
    '`\${cwd}`: ' + ( localizeWithPath(_moduleId, 0, "the terminal's current working directory")),
    '`\${cwdFolder}`: ' + ( localizeWithPath(
        _moduleId,
        1,
        "the terminal's current working directory, displayed for multi-root workspaces or in a single root workspace when the value differs from the initial working directory. On Windows, this will only be displayed when shell integration is enabled."
    )),
    '`\${workspaceFolder}`: ' + ( localizeWithPath(_moduleId, 2, "the workspace in which the terminal was launched")),
    '`\${local}`: ' + ( localizeWithPath(_moduleId, 3, "indicates a local terminal in a remote workspace")),
    '`\${process}`: ' + ( localizeWithPath(_moduleId, 4, "the name of the terminal process")),
    '`\${separator}`: ' + ( localizeWithPath(
        _moduleId,
        5,
        "a conditional separator {0} that only shows when surrounded by variables with values or static text.",
        '(` - `)'
    )),
    '`\${sequence}`: ' + ( localizeWithPath(_moduleId, 6, "the name provided to the terminal by the process")),
    '`\${task}`: ' + ( localizeWithPath(_moduleId, 7, "indicates this terminal is associated with a task")),
].join('\n- ');
let terminalTitle = ( localizeWithPath(
    _moduleId,
    8,
    "Controls the terminal title. Variables are substituted based on the context:"
));
terminalTitle += terminalDescriptors;
let terminalDescription = ( localizeWithPath(
    _moduleId,
    9,
    "Controls the terminal description, which appears to the right of the title. Variables are substituted based on the context:"
));
terminalDescription += terminalDescriptors;
const defaultTerminalFontSize = isMacintosh ? 12 : 14;
const terminalConfiguration = {
    id: 'terminal',
    order: 100,
    title: ( localizeWithPath(_moduleId, 10, "Integrated Terminal")),
    type: 'object',
    properties: {
        ["terminal.integrated.sendKeybindingsToShell" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                11,
                "Dispatches most keybindings to the terminal instead of the workbench, overriding {0}, which can be used alternatively for fine tuning.",
                '`#terminal.integrated.commandsToSkipShell#`'
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.tabs.defaultColor" ]: {
            description: ( localizeWithPath(
                _moduleId,
                12,
                "A theme color ID to associate with terminal icons by default."
            )),
            ...terminalColorSchema,
            scope: 4
        },
        ["terminal.integrated.tabs.defaultIcon" ]: {
            description: ( localizeWithPath(_moduleId, 13, "A codicon ID to associate with terminal icons by default.")),
            ...terminalIconSchema,
            default: Codicon.terminal.id,
            scope: 4
        },
        ["terminal.integrated.tabs.enabled" ]: {
            description: ( localizeWithPath(
                _moduleId,
                14,
                'Controls whether terminal tabs display as a list to the side of the terminal. When this is disabled a dropdown will display instead.'
            )),
            type: 'boolean',
            default: true,
        },
        ["terminal.integrated.tabs.enableAnimation" ]: {
            description: ( localizeWithPath(
                _moduleId,
                15,
                'Controls whether terminal tab statuses support animation (eg. in progress tasks).'
            )),
            type: 'boolean',
            default: true,
        },
        ["terminal.integrated.tabs.hideCondition" ]: {
            description: ( localizeWithPath(
                _moduleId,
                16,
                'Controls whether the terminal tabs view will hide under certain conditions.'
            )),
            type: 'string',
            enum: ['never', 'singleTerminal', 'singleGroup'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 17, "Never hide the terminal tabs view")),
                ( localizeWithPath(
                    _moduleId,
                    18,
                    "Hide the terminal tabs view when there is only a single terminal opened"
                )),
                ( localizeWithPath(
                    _moduleId,
                    19,
                    "Hide the terminal tabs view when there is only a single terminal group opened"
                )),
            ],
            default: 'singleTerminal',
        },
        ["terminal.integrated.tabs.showActiveTerminal" ]: {
            description: ( localizeWithPath(
                _moduleId,
                20,
                'Shows the active terminal information in the view. This is particularly useful when the title within the tabs aren\'t visible.'
            )),
            type: 'string',
            enum: ['always', 'singleTerminal', 'singleTerminalOrNarrow', 'never'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 21, "Always show the active terminal")),
                ( localizeWithPath(
                    _moduleId,
                    22,
                    "Show the active terminal when it is the only terminal opened"
                )),
                ( localizeWithPath(
                    _moduleId,
                    23,
                    "Show the active terminal when it is the only terminal opened or when the tabs view is in its narrow textless state"
                )),
                ( localizeWithPath(_moduleId, 24, "Never show the active terminal")),
            ],
            default: 'singleTerminalOrNarrow',
        },
        ["terminal.integrated.tabs.showActions" ]: {
            description: ( localizeWithPath(
                _moduleId,
                25,
                'Controls whether terminal split and kill buttons are displays next to the new terminal button.'
            )),
            type: 'string',
            enum: ['always', 'singleTerminal', 'singleTerminalOrNarrow', 'never'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 26, "Always show the actions")),
                ( localizeWithPath(_moduleId, 27, "Show the actions when it is the only terminal opened")),
                ( localizeWithPath(
                    _moduleId,
                    28,
                    "Show the actions when it is the only terminal opened or when the tabs view is in its narrow textless state"
                )),
                ( localizeWithPath(_moduleId, 29, "Never show the actions")),
            ],
            default: 'singleTerminalOrNarrow',
        },
        ["terminal.integrated.tabs.location" ]: {
            type: 'string',
            enum: ['left', 'right'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 30, "Show the terminal tabs view to the left of the terminal")),
                ( localizeWithPath(_moduleId, 31, "Show the terminal tabs view to the right of the terminal"))
            ],
            default: 'right',
            description: ( localizeWithPath(
                _moduleId,
                32,
                "Controls the location of the terminal tabs, either to the left or right of the actual terminal(s)."
            ))
        },
        ["terminal.integrated.defaultLocation" ]: {
            type: 'string',
            enum: ["editor" , "view" ],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 33, "Create terminals in the editor")),
                ( localizeWithPath(_moduleId, 34, "Create terminals in the terminal view"))
            ],
            default: 'view',
            description: ( localizeWithPath(_moduleId, 35, "Controls where newly created terminals will appear."))
        },
        ["terminal.integrated.tabs.focusMode" ]: {
            type: 'string',
            enum: ['singleClick', 'doubleClick'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 36, "Focus the terminal when clicking a terminal tab")),
                ( localizeWithPath(_moduleId, 37, "Focus the terminal when double-clicking a terminal tab"))
            ],
            default: 'doubleClick',
            description: ( localizeWithPath(
                _moduleId,
                38,
                "Controls whether focusing the terminal of a tab happens on double or single click."
            ))
        },
        ["terminal.integrated.macOptionIsMeta" ]: {
            description: ( localizeWithPath(
                _moduleId,
                39,
                "Controls whether to treat the option key as the meta key in the terminal on macOS."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.macOptionClickForcesSelection" ]: {
            description: ( localizeWithPath(
                _moduleId,
                40,
                "Controls whether to force selection when using Option+click on macOS. This will force a regular (line) selection and disallow the use of column selection mode. This enables copying and pasting using the regular terminal selection, for example, when mouse mode is enabled in tmux."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.altClickMovesCursor" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                41,
                "If enabled, alt/option + click will reposition the prompt cursor to underneath the mouse when {0} is set to {1} (the default value). This may not work reliably depending on your shell.",
                '`#editor.multiCursorModifier#`',
                '`\'alt\'`'
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.copyOnSelection" ]: {
            description: ( localizeWithPath(
                _moduleId,
                42,
                "Controls whether text selected in the terminal will be copied to the clipboard."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.enableMultiLinePasteWarning" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                43,
                "Controls whether to show a warning dialog when pasting multiple lines into the terminal."
            )),
            type: 'string',
            enum: ['auto', 'always', 'never'],
            markdownEnumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    44,
                    "Enable the warning but do not show it when:\n\n- Bracketed paste mode is enabled (the shell supports multi-line paste natively)\n- The paste is handled by the shell's readline (in the case of pwsh)"
                )),
                ( localizeWithPath(_moduleId, 45, "Always show the warning if the text contains a new line.")),
                ( localizeWithPath(_moduleId, 46, "Never show the warning."))
            ],
            default: 'auto'
        },
        ["terminal.integrated.drawBoldTextInBrightColors" ]: {
            description: ( localizeWithPath(
                _moduleId,
                47,
                "Controls whether bold text in the terminal will always use the \"bright\" ANSI color variant."
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.fontFamily" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                48,
                "Controls the font family of the terminal. Defaults to {0}'s value.",
                '`#editor.fontFamily#`'
            )),
            type: 'string'
        },
        ["terminal.integrated.fontSize" ]: {
            description: ( localizeWithPath(_moduleId, 49, "Controls the font size in pixels of the terminal.")),
            type: 'number',
            default: defaultTerminalFontSize,
            minimum: 6,
            maximum: 100
        },
        ["terminal.integrated.letterSpacing" ]: {
            description: ( localizeWithPath(
                _moduleId,
                50,
                "Controls the letter spacing of the terminal. This is an integer value which represents the number of additional pixels to add between characters."
            )),
            type: 'number',
            default: DEFAULT_LETTER_SPACING
        },
        ["terminal.integrated.lineHeight" ]: {
            description: ( localizeWithPath(
                _moduleId,
                51,
                "Controls the line height of the terminal. This number is multiplied by the terminal font size to get the actual line-height in pixels."
            )),
            type: 'number',
            default: DEFAULT_LINE_HEIGHT
        },
        ["terminal.integrated.minimumContrastRatio" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                52,
                "When set, the foreground color of each cell will change to try meet the contrast ratio specified. Note that this will not apply to `powerline` characters per #146406. Example values:\n\n- 1: Do nothing and use the standard theme colors.\n- 4.5: [WCAG AA compliance (minimum)](https://www.w3.org/TR/UNDERSTANDING-WCAG20/visual-audio-contrast-contrast.html) (default).\n- 7: [WCAG AAA compliance (enhanced)](https://www.w3.org/TR/UNDERSTANDING-WCAG20/visual-audio-contrast7.html).\n- 21: White on black or black on white."
            )),
            type: 'number',
            default: 4.5,
            tags: ['accessibility']
        },
        ["terminal.integrated.tabStopWidth" ]: {
            markdownDescription: ( localizeWithPath(_moduleId, 53, "The number of cells in a tab stop.")),
            type: 'number',
            minimum: 1,
            default: 8
        },
        ["terminal.integrated.fastScrollSensitivity" ]: {
            markdownDescription: ( localizeWithPath(_moduleId, 54, "Scrolling speed multiplier when pressing `Alt`.")),
            type: 'number',
            default: 5
        },
        ["terminal.integrated.mouseWheelScrollSensitivity" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                55,
                "A multiplier to be used on the `deltaY` of mouse wheel scroll events."
            )),
            type: 'number',
            default: 1
        },
        ["terminal.integrated.bellDuration" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                56,
                "The number of milliseconds to show the bell within a terminal tab when triggered."
            )),
            type: 'number',
            default: 1000
        },
        ["terminal.integrated.fontWeight" ]: {
            'anyOf': [
                {
                    type: 'number',
                    minimum: MINIMUM_FONT_WEIGHT,
                    maximum: MAXIMUM_FONT_WEIGHT,
                    errorMessage: ( localizeWithPath(
                        _moduleId,
                        57,
                        "Only \"normal\" and \"bold\" keywords or numbers between 1 and 1000 are allowed."
                    ))
                },
                {
                    type: 'string',
                    pattern: '^(normal|bold|1000|[1-9][0-9]{0,2})$'
                },
                {
                    enum: SUGGESTIONS_FONT_WEIGHT,
                }
            ],
            description: ( localizeWithPath(
                _moduleId,
                58,
                "The font weight to use within the terminal for non-bold text. Accepts \"normal\" and \"bold\" keywords or numbers between 1 and 1000."
            )),
            default: 'normal'
        },
        ["terminal.integrated.fontWeightBold" ]: {
            'anyOf': [
                {
                    type: 'number',
                    minimum: MINIMUM_FONT_WEIGHT,
                    maximum: MAXIMUM_FONT_WEIGHT,
                    errorMessage: ( localizeWithPath(
                        _moduleId,
                        57,
                        "Only \"normal\" and \"bold\" keywords or numbers between 1 and 1000 are allowed."
                    ))
                },
                {
                    type: 'string',
                    pattern: '^(normal|bold|1000|[1-9][0-9]{0,2})$'
                },
                {
                    enum: SUGGESTIONS_FONT_WEIGHT,
                }
            ],
            description: ( localizeWithPath(
                _moduleId,
                59,
                "The font weight to use within the terminal for bold text. Accepts \"normal\" and \"bold\" keywords or numbers between 1 and 1000."
            )),
            default: 'bold'
        },
        ["terminal.integrated.cursorBlinking" ]: {
            description: ( localizeWithPath(_moduleId, 60, "Controls whether the terminal cursor blinks.")),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.cursorStyle" ]: {
            description: ( localizeWithPath(
                _moduleId,
                61,
                "Controls the style of terminal cursor when the terminal is focused."
            )),
            enum: ['block', 'line', 'underline'],
            default: 'block'
        },
        ["terminal.integrated.cursorStyleInactive" ]: {
            description: ( localizeWithPath(
                _moduleId,
                62,
                "Controls the style of terminal cursor when the terminal is not focused."
            )),
            enum: ['outline', 'block', 'line', 'underline', 'none'],
            default: 'outline'
        },
        ["terminal.integrated.cursorWidth" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                63,
                "Controls the width of the cursor when {0} is set to {1}.",
                '`#terminal.integrated.cursorStyle#`',
                '`line`'
            )),
            type: 'number',
            default: 1
        },
        ["terminal.integrated.scrollback" ]: {
            description: ( localizeWithPath(
                _moduleId,
                64,
                "Controls the maximum number of lines the terminal keeps in its buffer. We pre-allocate memory based on this value in order to ensure a smooth experience. As such, as the value increases, so will the amount of memory."
            )),
            type: 'number',
            default: 1000
        },
        ["terminal.integrated.detectLocale" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                65,
                "Controls whether to detect and set the `$LANG` environment variable to a UTF-8 compliant option since VS Code's terminal only supports UTF-8 encoded data coming from the shell."
            )),
            type: 'string',
            enum: ['auto', 'off', 'on'],
            markdownEnumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    66,
                    "Set the `$LANG` environment variable if the existing variable does not exist or it does not end in `'.UTF-8'`."
                )),
                ( localizeWithPath(_moduleId, 67, "Do not set the `$LANG` environment variable.")),
                ( localizeWithPath(_moduleId, 68, "Always set the `$LANG` environment variable."))
            ],
            default: 'auto'
        },
        ["terminal.integrated.gpuAcceleration" ]: {
            type: 'string',
            enum: ['auto', 'on', 'off', 'canvas'],
            markdownEnumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    69,
                    "Let VS Code detect which renderer will give the best experience."
                )),
                ( localizeWithPath(_moduleId, 70, "Enable GPU acceleration within the terminal.")),
                ( localizeWithPath(
                    _moduleId,
                    71,
                    "Disable GPU acceleration within the terminal. The terminal will render much slower when GPU acceleration is off but it should reliably work on all systems."
                )),
                ( localizeWithPath(
                    _moduleId,
                    72,
                    "Use the terminal's fallback canvas renderer which uses a 2d context instead of webgl which may perform better on some systems. Note that some features are limited in the canvas renderer like opaque selection."
                ))
            ],
            default: 'auto',
            description: ( localizeWithPath(
                _moduleId,
                73,
                "Controls whether the terminal will leverage the GPU to do its rendering."
            ))
        },
        ["terminal.integrated.tabs.separator" ]: {
            'type': 'string',
            'default': ' - ',
            'markdownDescription': ( localizeWithPath(
                _moduleId,
                74,
                "Separator used by {0} and {1}.",
                `\`#${"terminal.integrated.tabs.title" }#\``,
                `\`#${"terminal.integrated.tabs.description" }#\``
            ))
        },
        ["terminal.integrated.tabs.title" ]: {
            'type': 'string',
            'default': '${process}',
            'markdownDescription': terminalTitle
        },
        ["terminal.integrated.tabs.description" ]: {
            'type': 'string',
            'default': '${task}${separator}${local}${separator}${cwdFolder}',
            'markdownDescription': terminalDescription
        },
        ["terminal.integrated.rightClickBehavior" ]: {
            type: 'string',
            enum: ['default', 'copyPaste', 'paste', 'selectWord', 'nothing'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 75, "Show the context menu.")),
                ( localizeWithPath(_moduleId, 76, "Copy when there is a selection, otherwise paste.")),
                ( localizeWithPath(_moduleId, 77, "Paste on right click.")),
                ( localizeWithPath(
                    _moduleId,
                    78,
                    "Select the word under the cursor and show the context menu."
                )),
                ( localizeWithPath(_moduleId, 79, "Do nothing and pass event to terminal."))
            ],
            default: isMacintosh ? 'selectWord' : isWindows ? 'copyPaste' : 'default',
            description: ( localizeWithPath(_moduleId, 80, "Controls how terminal reacts to right click."))
        },
        ["terminal.integrated.middleClickBehavior" ]: {
            type: 'string',
            enum: ['default', 'paste'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    81,
                    "The platform default to focus the terminal. On Linux this will also paste the selection."
                )),
                ( localizeWithPath(_moduleId, 82, "Paste on middle click.")),
            ],
            default: 'default',
            description: ( localizeWithPath(_moduleId, 83, "Controls how terminal reacts to middle click."))
        },
        ["terminal.integrated.cwd" ]: {
            restricted: true,
            description: ( localizeWithPath(
                _moduleId,
                84,
                "An explicit start path where the terminal will be launched, this is used as the current working directory (cwd) for the shell process. This may be particularly useful in workspace settings if the root directory is not a convenient cwd."
            )),
            type: 'string',
            default: undefined,
            scope: 4
        },
        ["terminal.integrated.confirmOnExit" ]: {
            description: ( localizeWithPath(
                _moduleId,
                85,
                "Controls whether to confirm when the window closes if there are active terminal sessions."
            )),
            type: 'string',
            enum: ['never', 'always', 'hasChildProcesses'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 86, "Never confirm.")),
                ( localizeWithPath(_moduleId, 87, "Always confirm if there are terminals.")),
                ( localizeWithPath(
                    _moduleId,
                    88,
                    "Confirm if there are any terminals that have child processes."
                )),
            ],
            default: 'never'
        },
        ["terminal.integrated.confirmOnKill" ]: {
            description: ( localizeWithPath(
                _moduleId,
                89,
                "Controls whether to confirm killing terminals when they have child processes. When set to editor, terminals in the editor area will be marked as changed when they have child processes. Note that child process detection may not work well for shells like Git Bash which don't run their processes as child processes of the shell."
            )),
            type: 'string',
            enum: ['never', 'editor', 'panel', 'always'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 90, "Never confirm.")),
                ( localizeWithPath(_moduleId, 91, "Confirm if the terminal is in the editor.")),
                ( localizeWithPath(_moduleId, 92, "Confirm if the terminal is in the panel.")),
                ( localizeWithPath(_moduleId, 93, "Confirm if the terminal is either in the editor or panel.")),
            ],
            default: 'editor'
        },
        ["terminal.integrated.enableBell" ]: {
            markdownDeprecationMessage: ( localizeWithPath(
                _moduleId,
                94,
                "This is now deprecated. Instead use the `terminal.integrated.enableVisualBell` and `accessibility.signals.terminalBell` settings."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.enableVisualBell" ]: {
            description: ( localizeWithPath(
                _moduleId,
                95,
                "Controls whether the visual terminal bell is enabled. This shows up next to the terminal's name."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.commandsToSkipShell" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                96,
                "A set of command IDs whose keybindings will not be sent to the shell but instead always be handled by VS Code. This allows keybindings that would normally be consumed by the shell to act instead the same as when the terminal is not focused, for example `Ctrl+P` to launch Quick Open.\n\n&nbsp;\n\nMany commands are skipped by default. To override a default and pass that command's keybinding to the shell instead, add the command prefixed with the `-` character. For example add `-workbench.action.quickOpen` to allow `Ctrl+P` to reach the shell.\n\n&nbsp;\n\nThe following list of default skipped commands is truncated when viewed in Settings Editor. To see the full list, {1} and search for the first command from the list below.\n\n&nbsp;\n\nDefault Skipped Commands:\n\n{0}",
                ( (DEFAULT_COMMANDS_TO_SKIP_SHELL.sort().map(command => `- ${command}`))).join('\n'),
                `[${( localizeWithPath(_moduleId, 97, "open the default settings JSON"))}](command:workbench.action.openRawDefaultSettings '${( localizeWithPath(_moduleId, 98, "Open Default Settings (JSON)"))}')`
            )),
            type: 'array',
            items: {
                type: 'string'
            },
            default: []
        },
        ["terminal.integrated.allowChords" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                99,
                "Whether or not to allow chord keybindings in the terminal. Note that when this is true and the keystroke results in a chord it will bypass {0}, setting this to false is particularly useful when you want ctrl+k to go to your shell (not VS Code).",
                '`#terminal.integrated.commandsToSkipShell#`'
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.allowMnemonics" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                100,
                "Whether to allow menubar mnemonics (for example Alt+F) to trigger the open of the menubar. Note that this will cause all alt keystrokes to skip the shell when true. This does nothing on macOS."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.env.osx" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                101,
                "Object with environment variables that will be added to the VS Code process to be used by the terminal on macOS. Set to `null` to delete the environment variable."
            )),
            type: 'object',
            additionalProperties: {
                type: ['string', 'null']
            },
            default: {}
        },
        ["terminal.integrated.env.linux" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                102,
                "Object with environment variables that will be added to the VS Code process to be used by the terminal on Linux. Set to `null` to delete the environment variable."
            )),
            type: 'object',
            additionalProperties: {
                type: ['string', 'null']
            },
            default: {}
        },
        ["terminal.integrated.env.windows" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                103,
                "Object with environment variables that will be added to the VS Code process to be used by the terminal on Windows. Set to `null` to delete the environment variable."
            )),
            type: 'object',
            additionalProperties: {
                type: ['string', 'null']
            },
            default: {}
        },
        ["terminal.integrated.environmentChangesIndicator" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                104,
                "Whether to display the environment changes indicator on each terminal which explains whether extensions have made, or want to make changes to the terminal's environment."
            )),
            type: 'string',
            enum: ['off', 'on', 'warnonly'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 105, "Disable the indicator.")),
                ( localizeWithPath(_moduleId, 106, "Enable the indicator.")),
                ( localizeWithPath(
                    _moduleId,
                    107,
                    "Only show the warning indicator when a terminal's environment is 'stale', not the information indicator that shows a terminal has had its environment modified by an extension."
                )),
            ],
            default: 'warnonly'
        },
        ["terminal.integrated.environmentChangesRelaunch" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                108,
                "Whether to relaunch terminals automatically if extensions want to contribute to their environment and have not been interacted with yet."
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.showExitAlert" ]: {
            description: ( localizeWithPath(
                _moduleId,
                109,
                "Controls whether to show the alert \"The terminal process terminated with exit code\" when exit code is non-zero."
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.splitCwd" ]: {
            description: ( localizeWithPath(
                _moduleId,
                110,
                "Controls the working directory a split terminal starts with."
            )),
            type: 'string',
            enum: ['workspaceRoot', 'initial', 'inherited'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    111,
                    "A new split terminal will use the workspace root as the working directory. In a multi-root workspace a choice for which root folder to use is offered."
                )),
                ( localizeWithPath(
                    _moduleId,
                    112,
                    "A new split terminal will use the working directory that the parent terminal started with."
                )),
                ( localizeWithPath(
                    _moduleId,
                    113,
                    "On macOS and Linux, a new split terminal will use the working directory of the parent terminal. On Windows, this behaves the same as initial."
                )),
            ],
            default: 'inherited'
        },
        ["terminal.integrated.windowsEnableConpty" ]: {
            description: ( localizeWithPath(
                _moduleId,
                114,
                "Whether to use ConPTY for Windows terminal process communication (requires Windows 10 build number 18309+). Winpty will be used if this is false."
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.wordSeparators" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                115,
                "A string containing all characters to be considered word separators when double-clicking to select word and in the fallback 'word' link detection. Since this is used for link detection, including characters such as `:` that are used when detecting links will cause the line and column part of links like `file:10:5` to be ignored."
            )),
            type: 'string',
            default: ' ()[]{}\',"`─‘’“”|'
        },
        ["terminal.integrated.enableFileLinks" ]: {
            description: ( localizeWithPath(
                _moduleId,
                116,
                "Whether to enable file links in terminals. Links can be slow when working on a network drive in particular because each file link is verified against the file system. Changing this will take effect only in new terminals."
            )),
            type: 'string',
            enum: ['off', 'on', 'notRemote'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 117, "Always off.")),
                ( localizeWithPath(_moduleId, 118, "Always on.")),
                ( localizeWithPath(_moduleId, 119, "Enable only when not in a remote workspace."))
            ],
            default: 'on'
        },
        ["terminal.integrated.allowedLinkSchemes" ]: {
            description: ( localizeWithPath(
                _moduleId,
                120,
                "An array of strings containing the URI schemes that the terminal is allowed to open links for. By default, only a small subset of possible schemes are allowed for security reasons."
            )),
            type: 'array',
            items: {
                type: 'string'
            },
            default: [
                'file',
                'http',
                'https',
                'mailto',
                'vscode',
                'vscode-insiders',
            ]
        },
        ["terminal.integrated.unicodeVersion" ]: {
            type: 'string',
            enum: ['6', '11'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    121,
                    "Version 6 of Unicode. This is an older version which should work better on older systems."
                )),
                ( localizeWithPath(
                    _moduleId,
                    122,
                    "Version 11 of Unicode. This version provides better support on modern systems that use modern versions of Unicode."
                ))
            ],
            default: '11',
            description: ( localizeWithPath(
                _moduleId,
                123,
                "Controls what version of Unicode to use when evaluating the width of characters in the terminal. If you experience emoji or other wide characters not taking up the right amount of space or backspace either deleting too much or too little then you may want to try tweaking this setting."
            ))
        },
        ["terminal.integrated.localEchoLatencyThreshold" ]: {
            description: ( localizeWithPath(
                _moduleId,
                124,
                "Length of network delay, in milliseconds, where local edits will be echoed on the terminal without waiting for server acknowledgement. If '0', local echo will always be on, and if '-1' it will be disabled."
            )),
            type: 'integer',
            minimum: -1,
            default: 30,
        },
        ["terminal.integrated.localEchoEnabled" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                125,
                "When local echo should be enabled. This will override {0}",
                '`#terminal.integrated.localEchoLatencyThreshold#`'
            )),
            type: 'string',
            enum: ['on', 'off', 'auto'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 126, "Always enabled")),
                ( localizeWithPath(_moduleId, 127, "Always disabled")),
                ( localizeWithPath(_moduleId, 128, "Enabled only for remote workspaces"))
            ],
            default: 'auto'
        },
        ["terminal.integrated.localEchoExcludePrograms" ]: {
            description: ( localizeWithPath(
                _moduleId,
                129,
                "Local echo will be disabled when any of these program names are found in the terminal title."
            )),
            type: 'array',
            items: {
                type: 'string',
                uniqueItems: true
            },
            default: DEFAULT_LOCAL_ECHO_EXCLUDE,
        },
        ["terminal.integrated.localEchoStyle" ]: {
            description: ( localizeWithPath(
                _moduleId,
                130,
                "Terminal style of locally echoed text; either a font style or an RGB color."
            )),
            default: 'dim',
            anyOf: [
                {
                    enum: ['bold', 'dim', 'italic', 'underlined', 'inverted', '#ff0000'],
                },
                {
                    type: 'string',
                    format: 'color-hex',
                }
            ]
        },
        ["terminal.integrated.enablePersistentSessions" ]: {
            description: ( localizeWithPath(
                _moduleId,
                131,
                "Persist terminal sessions/history for the workspace across window reloads."
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.persistentSessionReviveProcess" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                132,
                "When the terminal process must be shut down (for example on window or application close), this determines when the previous terminal session contents/history should be restored and processes be recreated when the workspace is next opened.\n\nCaveats:\n\n- Restoring of the process current working directory depends on whether it is supported by the shell.\n- Time to persist the session during shutdown is limited, so it may be aborted when using high-latency remote connections."
            )),
            type: 'string',
            enum: ['onExit', 'onExitAndWindowClose', 'never'],
            markdownEnumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    133,
                    "Revive the processes after the last window is closed on Windows/Linux or when the `workbench.action.quit` command is triggered (command palette, keybinding, menu)."
                )),
                ( localizeWithPath(
                    _moduleId,
                    134,
                    "Revive the processes after the last window is closed on Windows/Linux or when the `workbench.action.quit` command is triggered (command palette, keybinding, menu), or when the window is closed."
                )),
                ( localizeWithPath(
                    _moduleId,
                    135,
                    "Never restore the terminal buffers or recreate the process."
                ))
            ],
            default: 'onExit'
        },
        ["terminal.integrated.hideOnStartup" ]: {
            description: ( localizeWithPath(
                _moduleId,
                136,
                "Whether to hide the terminal view on startup, avoiding creating a terminal when there are no persistent sessions."
            )),
            type: 'string',
            enum: ['never', 'whenEmpty', 'always'],
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 137, "Never hide the terminal view on startup.")),
                ( localizeWithPath(
                    _moduleId,
                    138,
                    "Only hide the terminal when there are no persistent sessions restored."
                )),
                ( localizeWithPath(
                    _moduleId,
                    139,
                    "Always hide the terminal, even when there are persistent sessions restored."
                ))
            ],
            default: 'never'
        },
        ["terminal.integrated.customGlyphs" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                140,
                "Whether to draw custom glyphs for block element and box drawing characters instead of using the font, which typically yields better rendering with continuous lines. Note that this doesn't work when {0} is disabled.",
                `\`#${"terminal.integrated.gpuAcceleration" }#\``
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.rescaleOverlappingGlyphs" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                141,
                "Whether to rescale glyphs horizontally that are a single cell wide but have glyphs that would overlap following cell(s). This typically happens for ambiguous width characters (eg. the roman numeral characters U+2160+) which aren't featured in monospace fonts. Emoji glyphs are never rescaled."
            )),
            type: 'boolean',
            default: product$1.quality !== 'stable'
        },
        ["terminal.integrated.autoReplies" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                142,
                "A set of messages that, when encountered in the terminal, will be automatically responded to. Provided the message is specific enough, this can help automate away common responses.\n\nRemarks:\n\n- Use {0} to automatically respond to the terminate batch job prompt on Windows.\n- The message includes escape sequences so the reply might not happen with styled text.\n- Each reply can only happen once every second.\n- Use {1} in the reply to mean the enter key.\n- To unset a default key, set the value to null.\n- Restart VS Code if new don't apply.",
                '`"Terminate batch job (Y/N)": "Y\\r"`',
                '`"\\r"`'
            )),
            type: 'object',
            additionalProperties: {
                oneOf: [{
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 143, "The reply to send to the process."))
                    },
                    { type: 'null' }]
            },
            default: {}
        },
        ["terminal.integrated.shellIntegration.enabled" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                144,
                "Determines whether or not shell integration is auto-injected to support features like enhanced command tracking and current working directory detection. \n\nShell integration works by injecting the shell with a startup script. The script gives VS Code insight into what is happening within the terminal.\n\nSupported shells:\n\n- Linux/macOS: bash, fish, pwsh, zsh\n - Windows: pwsh, git bash\n\nThis setting applies only when terminals are created, so you will need to restart your terminals for it to take effect.\n\n Note that the script injection may not work if you have custom arguments defined in the terminal profile, have enabled {1}, have a [complex bash `PROMPT_COMMAND`](https://code.visualstudio.com/docs/editor/integrated-terminal#_complex-bash-promptcommand), or other unsupported setup. To disable decorations, see {0}",
                '`#terminal.integrated.shellIntegrations.decorationsEnabled#`',
                '`#editor.accessibilitySupport#`'
            )),
            type: 'boolean',
            default: true
        },
        ["terminal.integrated.shellIntegration.decorationsEnabled" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                145,
                "When shell integration is enabled, adds a decoration for each command."
            )),
            type: 'string',
            enum: ['both', 'gutter', 'overviewRuler', 'never'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    146,
                    "Show decorations in the gutter (left) and overview ruler (right)"
                )),
                ( localizeWithPath(_moduleId, 147, "Show gutter decorations to the left of the terminal")),
                ( localizeWithPath(
                    _moduleId,
                    148,
                    "Show overview ruler decorations to the right of the terminal"
                )),
                ( localizeWithPath(_moduleId, 149, "Do not show decorations")),
            ],
            default: 'both'
        },
        ["terminal.integrated.shellIntegration.history" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                150,
                "Controls the number of recently used commands to keep in the terminal command history. Set to 0 to disable terminal command history."
            )),
            type: 'number',
            default: 100
        },
        ["terminal.integrated.shellIntegration.suggestEnabled" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                151,
                "Enables experimental terminal Intellisense suggestions for supported shells when {0} is set to {1}. If shell integration is installed manually, {2} needs to be set to {3} before calling the script.",
                '`#terminal.integrated.shellIntegration.enabled#`',
                '`true`',
                '`VSCODE_SUGGEST`',
                '`1`'
            )),
            type: 'boolean',
            default: false,
            markdownDeprecationMessage: ( localizeWithPath(
                _moduleId,
                152,
                'This is an experimental setting and may break the terminal! Use at your own risk.'
            ))
        },
        ["terminal.integrated.smoothScrolling" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                153,
                "Controls whether the terminal will scroll using an animation."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.ignoreBracketedPasteMode" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                154,
                "Controls whether the terminal will ignore bracketed paste mode even if the terminal was put into the mode, omitting the {0} and {1} sequences when pasting. This is useful when the shell is not respecting the mode which can happen in sub-shells for example.",
                '`\\x1b[200~`',
                '`\\x1b[201~`'
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.enableImages" ]: {
            restricted: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                155,
                "Enables image support in the terminal, this will only work when {0} is enabled. Both sixel and iTerm's inline image protocol are supported on Linux and macOS, Windows support will light up automatically when ConPTY passes through the sequences. Images will currently not be restored between window reloads/reconnects.",
                `\`#${"terminal.integrated.gpuAcceleration" }#\``
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.focusAfterRun" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                156,
                "Controls whether the terminal, accessible buffer, or neither will be focused after `Terminal: Run Selected Text In Active Terminal` has been run."
            )),
            enum: ['terminal', 'accessible-buffer', 'none'],
            default: 'none',
            tags: ['accessibility'],
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 157, "Always focus the terminal.")),
                ( localizeWithPath(_moduleId, 158, "Always focus the accessible buffer.")),
                ( localizeWithPath(_moduleId, 159, "Do nothing.")),
            ]
        },
        ["terminal.integrated.accessibleViewPreserveCursorPosition" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                160,
                "Preserve the cursor position on reopen of the terminal's accessible view rather than setting it to the bottom of the buffer."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.accessibleViewFocusOnCommandExecution" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                161,
                "Focus the terminal accessible view when a command is executed."
            )),
            type: 'boolean',
            default: false
        },
        ["terminal.integrated.stickyScroll.enabled" ]: {
            markdownDescription: ( localizeWithPath(_moduleId, 162, "Shows the current command at the top of the terminal.")),
            type: 'boolean',
            default: product$1.quality !== 'stable'
        },
        ["terminal.integrated.stickyScroll.maxLineCount" ]: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                163,
                "Defines the maximum number of sticky lines to show. Sticky scroll lines will never exceed 40% of the viewport regardless of this setting."
            )),
            type: 'number',
            default: 5,
            minimum: 1,
            maximum: 10
        },
        ["terminal.integrated.mouseWheelZoom" ]: {
            markdownDescription: isMacintosh
                ? ( localizeWithPath(
                _moduleId,
                164,
                "Zoom the font of the terminal when using mouse wheel and holding `Cmd`."
            ))
                : ( localizeWithPath(
                _moduleId,
                165,
                "Zoom the font of the terminal when using mouse wheel and holding `Ctrl`."
            )),
            type: 'boolean',
            default: false
        },
    }
};
function registerTerminalConfiguration() {
    const configurationRegistry = ( (Registry.as(Extensions.Configuration)));
    configurationRegistry.registerConfiguration(terminalConfiguration);
}
( (Registry.as(Extensions$1.ConfigurationMigration)))
    .registerConfigurationMigrations([{
        key: "terminal.integrated.enableBell" ,
        migrateFn: (enableBell, accessor) => {
            const configurationKeyValuePairs = [];
            let announcement = accessor('accessibility.signals.terminalBell')?.announcement ?? accessor('accessibility.alert.terminalBell');
            if (announcement !== undefined && typeof announcement !== 'string') {
                announcement = announcement ? 'auto' : 'off';
            }
            configurationKeyValuePairs.push(['accessibility.signals.terminalBell', { value: { sound: enableBell ? 'on' : 'off', announcement } }]);
            configurationKeyValuePairs.push(["terminal.integrated.enableBell" , { value: undefined }]);
            configurationKeyValuePairs.push(["terminal.integrated.enableVisualBell" , { value: enableBell }]);
            return configurationKeyValuePairs;
        }
    }]);

export { defaultTerminalFontSize, registerTerminalConfiguration };
